import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COLUMN_GROUPS, ColumnGroup } from '../../../services/excel/ColumnGroups';

interface ColumnTreePickerProps {
    defaultExpanded?: boolean;
    groups?: ColumnGroup[];
    value: string[];
    onChange: (keys: string[]) => void;
}

const ColumnTreePicker: React.FC<ColumnTreePickerProps> = ({ value, onChange, groups = COLUMN_GROUPS, defaultExpanded = false }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState<Set<string>>(defaultExpanded ? new Set(groups.map((g) => g.key)) : new Set());

    const groupRefs = useRef<Record<string, HTMLInputElement | null>>({});

    useEffect(() => {
        groups.forEach((group) => {
            const el = groupRefs.current[group.key];
            if (el) {
                const selectedCount = group.columns.filter((col) => value.includes(col.key)).length;
                const isPartial = selectedCount > 0 && selectedCount < group.columns.length;
                el.indeterminate = isPartial;
            }
        });
    }, [groups, value]);

    const toggleExpand = (groupKey: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }
            return next;
        });
    };

    const handleGroupToggle = (group: ColumnGroup) => {
        const groupKeys = group.columns.map((col) => col.key);
        const allSelected = groupKeys.every((k) => value.includes(k));
        if (allSelected) {
            onChange(value.filter((k) => !groupKeys.includes(k)));
        } else {
            const newKeys = [...value];
            groupKeys.forEach((k) => {
                if (!newKeys.includes(k)) {
                    newKeys.push(k);
                }
            });
            onChange(newKeys);
        }
    };

    const handleColumnToggle = (colKey: string) => {
        if (value.includes(colKey)) {
            onChange(value.filter((k) => k !== colKey));
        } else {
            onChange([...value, colKey]);
        }
    };

    return (
        <div className="column-tree-picker space-y-2">
            {groups.map((group) => {
                const selectedCount = group.columns.filter((col) => value.includes(col.key)).length;
                const allSelected = selectedCount === group.columns.length;
                const isPartial = selectedCount > 0 && selectedCount < group.columns.length;
                const isExpanded = expanded.has(group.key);
                const groupLabel = t(group.labelKey);

                return (
                    <div key={group.key} className="border border-gray-200 rounded-md">
                        {/* Group header */}
                        <div
                            role="button"
                            tabIndex={0}
                            aria-expanded={isExpanded}
                            onClick={() => toggleExpand(group.key)}
                            onKeyDown={(event) => {
                                if (event.key !== 'Enter' && event.key !== ' ') {
                                    return;
                                }

                                event.preventDefault();
                                toggleExpand(group.key);
                            }}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-t-md cursor-pointer"
                        >
                            <input
                                type="checkbox"
                                ref={(el) => {
                                    groupRefs.current[group.key] = el;
                                }}
                                checked={allSelected}
                                onClick={(event) => event.stopPropagation()}
                                onKeyDown={(event) => event.stopPropagation()}
                                onChange={() => handleGroupToggle(group)}
                                aria-checked={isPartial ? 'mixed' : allSelected}
                                aria-label={t('management.excelExport.columns.aria.selectGroup', { group: groupLabel })}
                                className="cursor-pointer h-4 w-4 rounded"
                            />
                            <span className="font-medium text-sm flex-1">{groupLabel}</span>
                            <button
                                type="button"
                                aria-expanded={isExpanded}
                                aria-label={
                                    isExpanded
                                        ? t('management.excelExport.columns.aria.collapseGroup')
                                        : t('management.excelExport.columns.aria.expandGroup')
                                }
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggleExpand(group.key);
                                }}
                                className="text-gray-500 hover:text-gray-700 text-xs"
                            >
                                {isExpanded ? '▲' : '▼'}
                            </button>
                        </div>
                        {/* Columns */}
                        {isExpanded && (
                            <div className="px-3 py-1 space-y-1">
                                {group.columns.map((col) => (
                                    <label key={col.key} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={value.includes(col.key)}
                                            onChange={() => handleColumnToggle(col.key)}
                                            className="cursor-pointer h-4 w-4 rounded"
                                        />
                                        {t(col.uiLabelKey)}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ColumnTreePicker;
